import { TableIdSchema } from "@/server/schemas/table-tables";
import { z } from "zod";

export const FilterOperatorSchema = z.union([
  z.literal("exact"),
  z.literal("contains"),
  z.literal("icontains"),
  z.literal("in"),
  z.literal("startswith"),
  z.literal("gte"),
  z.literal("gt"),
  z.literal("lte"),
  z.literal("lt"),
  z.literal("range"),
]);

export type FilterConditionOperators = z.infer<typeof FilterOperatorSchema>;

export const RecordFilterSchema = z.object({
  key: z.string(),
  operator: FilterOperatorSchema.optional(),
  value: z.string(),
});

export const RecordOrderSchema = z.object({
  key: z.string().optional(),
  direction: z.union([z.literal("asc"), z.literal("desc")]).optional(),
});

export const RecordCursorSchema = z.object({
  query_type: z.literal("window"),
  start_cursor: z.string().optional(),
  end_cursor: z.string().optional(),
});

export const RecordBeforeCursorSchema = z.object({
  query_type: z.literal("before"),
  before_cursor: z.string().optional(),
});

export const RecordCursorQuerySchema = z.discriminatedUnion("query_type", [
  RecordCursorSchema,
  RecordBeforeCursorSchema,
]);

export const RecordQuerySchema = z.object({
  filters: RecordFilterSchema.array().optional(),
  orders: RecordOrderSchema.array().optional(),
  cursor_query: RecordCursorQuerySchema.optional(),
  limit: z.number().int().optional(),
});

const RecordCountQuerySchema = z.object({
  filters: RecordFilterSchema.array().optional(),
  orders: RecordOrderSchema.array().optional(),
});

export const listRecordsSchema = z.object({
  tableId: TableIdSchema,
  filter: RecordQuerySchema,
  cursor: RecordQuerySchema.shape.cursor_query,
  fields: z.string().array().optional(),
});

export const tableRecordsListSchema = z.object({
  tableId: TableIdSchema,
  filter: RecordQuerySchema,
  cursor: z.string().optional(),
  fields: z.string().array().optional(),
});

export const countRecordsSchema = z.object({
  tableId: TableIdSchema,
  filter: RecordCountQuerySchema,
});

export type RecordFilter = z.infer<typeof RecordFilterSchema>;
export type RecordOrder = z.infer<typeof RecordOrderSchema>;

export type RecordQuery = z.infer<typeof RecordQuerySchema>;
export type CursorQuery = z.infer<typeof RecordQuerySchema.shape.cursor_query>;

export const getRecordSchema = z.object({
  tableId: TableIdSchema,
  recordId: z.string(),
  fields: z.string().array().optional(),
});

export const createRecordsSchema = z.object({
  tableId: TableIdSchema,
  body: z.object({
    new_records: z
      .object({
        data: z.record(z.unknown()),
      })
      .array(),
    change_options: z.record(z.unknown()).optional(),
  }),
});

export type CreateRecordsBody = z.infer<typeof createRecordsSchema>["body"];

export const updateRecordsSchema = z.object({
  tableId: TableIdSchema,
  body: z.object({
    updated_records: z
      .object({
        id: z.string(),
        data: z.record(z.unknown()),
      })
      .array(),
    change_options: z.record(z.unknown()).optional(),
  }),
  fields: z.string().array().optional(),
});

export type UpdateRecordsBody = z.infer<typeof updateRecordsSchema>["body"];

export const deleteRecordsSchema = z.object({
  tableId: TableIdSchema,
  body: z.object({
    record_ids: z.string().array(),
    change_options: z.any(),
  }),
});

export type DeleteRecordsBody = z.infer<typeof deleteRecordsSchema>["body"];

export const deliverRecordChangesSchema = z.object({
  recordId: z.string(),
  tableId: TableIdSchema,
});

/**
 * This schema does not include _all_ properties of the record.
 * It only includes the properties relevant to our usage across various blocks.
 */
export const tableRecordSchema = z.object({
  data: z.record(z.string(), z.unknown()),
  id: z.string(),
  edited_at: z.string(),
  created_at: z.string(),
  errors: z.record(z.string(), z.any()).optional(),
  schema_revision_id: z.number(),
});

export type TableRecord = z.infer<typeof tableRecordSchema>;

/**
 * This schema does not include _all_ properties of the record.
 * It only includes the properties relevant to our usage across various blocks.
 */
export const createTableRecordChangeSchema = z.object({
  change_id: z.string().optional(),
  old: tableRecordSchema.optional(),
  new: tableRecordSchema.optional(),
});

/**
 * This schema does not include _all_ properties of the record.
 * It only includes the properties relevant to our usage across various blocks.
 */
export const updateTableRecordChangeSchema = z.object({
  change_id: z.string().optional(),
  old: tableRecordSchema.optional(),
  new: tableRecordSchema.optional(),
});

/**
 * This schema does not include _all_ properties of the record.
 * It only includes the properties relevant to our usage across various blocks.
 */
export const deleteTableRecordChangeSchema = z.object({
  change_id: z.string().optional(),
  old: tableRecordSchema.optional(),
  new: tableRecordSchema.optional(),
});
